/**
 * Steps III and IV of the briefing: Ground (where) and Deploy (the final check).
 *
 * The two used to be one tab. Splitting them is what makes the rail honest — step III is
 * where the player *chooses* the mode, the ground and the squad, step IV only *reads back*
 * what steps I–III decided and offers the one irreversible button. Neither holds the
 * choice: the draft lives in the shell (`useDeployDraft`) so switching steps never loses it.
 *
 * Two things are deliberately shut in this build (Chris, 2026-09-04): Raid is on the board
 * but not playable — Expedition is the only mode that leaves the hub — and squads are a
 * label rather than a field, so the `squad` string the host forwards is always ''. Both
 * stay visible rather than hidden: a missing feature you can see is a promise, a missing
 * feature you cannot see is a smaller game.
 */

import type { VNode } from 'preact';
import { useEffect } from 'preact/hooks';
import {
  CALLINGS,
  DEFAULT_MAP_ID,
  ITEMS,
  ItemLocation,
  MAPS,
  QUICK_SLOTS,
  SATCHEL_SLOTS,
  STARTER_GROUND_ID,
  type CallingDef,
  type ItemStack,
  type MapDef,
} from '@stillsun/shared';
import type { HubHost } from './index.js';
import { Contracts } from './Contracts.js';
import { CALLING_ICON, Icon, type IconName, isIconName, itemKindIcon } from '../icons.js';
import {
  Cap,
  DEBUG_GROUND,
  type DeployDraftHandle,
  type DeployMode,
  type HubController,
  PLAYABLE_MODE,
  Section,
  playableMode,
  useSession,
} from './common.js';

interface ModeCard {
  id: DeployMode;
  label: string;
  meta: string;
  blurb: string;
  icon: IconName;
  /** False keeps the chevron on the board but takes it out of the tab order entirely. */
  playable: boolean;
}

const MODES: ModeCard[] = [
  {
    id: 'expedition',
    label: 'Expedition',
    meta: '1–4 players',
    blurb: 'One to four, Hymns only. Slower, longer, quieter.',
    icon: 'expedition',
    playable: true,
  },
  {
    id: 'raid',
    label: 'Raid',
    meta: '12 players',
    blurb: 'Twelve Wakers together against the Downs, no friends you did not bring.',
    icon: 'raid',
    playable: false,
  },
];

/** Copy under the ground's own name — the owner could not tell the two apart without it. */
const GROUND_BLURB: Record<string, string> = {
  lanternfall:
    'The last lit city. Walls, a gate, the Lantern Hall and Sabel at her counter — and everyone else who is still standing.',
  'vigil-downs':
    'The road out of Lanternfall as it climbs into the Downs — barrows either side, standing stones ahead, a lantern post at every crossing. You can walk here through the gate; this drops you straight in.',
  spine: 'the wreck · dev',
  yard: 'training pad · dev',
};

/** A stable, shareable-looking handle derived from the player id. Display only in M2. */
function friendCodeFor(id: number | undefined): string {
  if (typeof id !== 'number' || !Number.isFinite(id)) return 'LF-----';
  return `LF-${Math.abs(Math.trunc(id)).toString(36).toUpperCase().padStart(4, '0')}`;
}

export function mapName(mapId: string): string {
  return MAPS[mapId]?.name ?? 'unsurveyed';
}

export function modeName(mode: DeployMode): string {
  return mode === 'raid' ? 'Raid' : 'Expedition';
}

/** The one line step III shows on its ribbon. */
export function groundSummary(draft: { mode: DeployMode; mapId: string }): string {
  return `${modeName(playableMode(draft.mode))} · ${mapName(draft.mapId)}`;
}

/**
 * The grounds a player may actually see: the persistent world, and nothing else.
 *
 * Since v3 that is Lanternfall and the Vigil Downs — one city and one zone, both always there.
 * The Yard and the Spine survive in `MAPS` because The Waking still runs on the Yard and the
 * wreck is still a fixture the tests stand on, but neither is a place a player deploys to, so
 * `?debug` is the only way either appears on this board. Ordered so the city is always first:
 * `MAPS` is a record and its insertion order is not a promise anybody made.
 */
export function visibleMaps(): MapDef[] {
  const world = Object.values(MAPS).filter((m) => m.persistent === true);
  const ordered = [
    ...world.filter((m) => m.id === DEFAULT_MAP_ID),
    ...world.filter((m) => m.id !== DEFAULT_MAP_ID),
  ];
  if (!DEBUG_GROUND) return ordered;
  return [...ordered, ...Object.values(MAPS).filter((m) => m.persistent !== true)];
}

// ------------------------------------------------------------------ step III

export interface GroundProps {
  deploy: DeployDraftHandle;
  nonce: number;
  token: string;
  /** Picking the ground advances the rail, exactly once, on the click. */
  onPicked: () => void;
}

export function Ground(props: GroundProps): VNode {
  const { deploy } = props;
  const auth = useSession();
  const maps = visibleMaps();
  const chosenId = maps.some((m) => m.id === deploy.draft.mapId)
    ? deploy.draft.mapId
    : (maps[0]?.id ?? '');

  return (
    <div class="hub-pane">
      <Section title="Mode" icon="expedition" hint="Raid opens once the Downs can hold twelve">
        <div class="hub-modes">
          {MODES.map((m) => {
            // A native `disabled` is what takes Raid out of the tab order; the class only
            // has to kill the hover the skin would otherwise still paint on it.
            const on = m.playable && deploy.draft.mode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                class={`hub-chev${on ? ' on' : ''}${m.playable ? '' : ' soon'}`}
                disabled={!m.playable}
                aria-pressed={on ? 'true' : 'false'}
                onClick={() => {
                  if (!m.playable) return;
                  deploy.set({ mode: m.id });
                }}
              >
                <span class="hub-cap">
                  <Icon name={m.icon} size={14} />
                  Mode
                </span>
                <span class="hub-chev-title">{m.label}</span>
                <span class="hub-chev-meta">
                  {m.playable ? m.meta : <span class="hub-soon">not open yet</span>}
                </span>
                <span class="hub-chev-blurb">{m.blurb}</span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section
        title="Ground"
        icon="ground"
        hint={maps.length === 1 ? 'More opens as the Downs are surveyed' : ''}
      >
        {maps.length === 0 ? (
          <p class="hub-notice">No ground is surveyed yet. Nothing to drop onto.</p>
        ) : (
          <div class="hub-maps">
            {maps.map((m) => (
              <button
                key={m.id}
                type="button"
                class={`hub-chev${chosenId === m.id ? ' on' : ''}`}
                aria-pressed={chosenId === m.id ? 'true' : 'false'}
                onClick={() => {
                  deploy.set({ mapId: m.id });
                  props.onPicked();
                }}
              >
                <span class="hub-cap">
                  <Icon name={m.persistent === true ? 'ground' : 'deck'} size={14} />
                  Ground
                </span>
                <span class="hub-chev-title">{m.name}</span>
                <span class="hub-chev-meta">
                  {String(m.spawns.length)} drop points
                  {typeof m.raidDurationMs === 'number'
                    ? ` · ${String(Math.round(m.raidDurationMs / 60000))} min`
                    : ''}
                </span>
                <span class="hub-chev-blurb">{GROUND_BLURB[m.id] ?? ''}</span>
              </button>
            ))}
          </div>
        )}
      </Section>

      <Section title="Squad" icon="squad" hint="">
        <div class="hub-lines">
          <div class="hub-line">
            <span class="hub-line-k">
              <Icon name="squad" />
              Squad
            </span>
            <span class="hub-line-v">
              <span class="hub-soon">not open yet</span>
            </span>
          </div>
          <div class="hub-line">
            <span class="hub-line-k">
              <Icon name="account" />
              Your code
            </span>
            <span class="hub-line-v">
              <b class="hub-code">{friendCodeFor(auth.player?.id)}</b>
            </span>
          </div>
        </div>
        <p class="hub-notice">
          Keep it somewhere. When friend codes go live it is the handle people will add you by;
          until then everyone drops alone.
        </p>
      </Section>

      <Contracts nonce={props.nonce} token={props.token} />
    </div>
  );
}

// ------------------------------------------------------------------- step IV

/** The stacks the player is actually taking out, in the order they matter. */
function carrying(
  items: ItemStack[],
): { name: string; meta: string; insured: boolean; icon: IconName }[] {
  const rows: { name: string; meta: string; insured: boolean; icon: IconName }[] = [];
  const push = (stack: ItemStack, meta: string): void => {
    const def = ITEMS[stack.itemId];
    rows.push({
      name: `${def?.name ?? stack.itemId}${stack.qty > 1 ? ` ×${String(stack.qty)}` : ''}`,
      meta,
      insured: stack.insured,
      icon: itemKindIcon(def?.kind),
    });
  };
  for (const s of items) if (s.location === ItemLocation.Primary) push(s, 'primary');
  for (const s of items) if (s.location === ItemLocation.Secondary) push(s, 'secondary');
  for (const s of items)
    if (s.location === ItemLocation.Quick) push(s, `quick ${String(s.slot + 1)}`);
  for (const s of items)
    if (s.location === ItemLocation.Satchel) push(s, `satchel ${String(s.slot + 1)}`);
  return rows;
}

export interface DeployProps {
  host: HubHost;
  ctl: HubController;
  deploy: DeployDraftHandle;
  /** null while the stash is loading or the ledger is silent. */
  items: ItemStack[] | null;
  /**
   * 0 means "no Calling taken yet"; null means the ledger did not answer. Meaningless
   * until `progressLoaded` — "we have not asked yet" and "we asked and got nothing" are
   * different answers and only the second one may open the gate.
   */
  calling: number | null;
  progressLoaded: boolean;
  /**
   * Step III has not opened yet (below level 2). Deploy stops asking "where" and answers it:
   * there is exactly one ground a new Waker can stand on, so the tab preselects it rather than
   * showing a chooser with one item in it and a locked tab behind it.
   */
  groundLocked: boolean;
  /** The Waking is behind them. A returning Waker gets one button, not a briefing. */
  onboarded: boolean;
}

export function Deploy(props: DeployProps): VNode {
  const { ctl, deploy, items } = props;
  const maps = visibleMaps();

  // While Ground is shut there is one answer, and it is not the draft's business to remember a
  // different one. Written into the draft rather than merely substituted at send time, so the
  // ribbon, the summary line and the request can never disagree about where this is going.
  const starter = maps.find((m) => m.id === STARTER_GROUND_ID) ?? maps[0] ?? null;
  useEffect(() => {
    if (!props.groundLocked || starter === null) return;
    if (deploy.draft.mapId === starter.id && deploy.draft.mode === PLAYABLE_MODE) return;
    deploy.set({ mapId: starter.id, mode: PLAYABLE_MODE });
  }, [props.groundLocked, starter, deploy]);

  const chosen = maps.find((m) => m.id === deploy.draft.mapId) ?? maps[0] ?? null;

  // Three states, not two. The ledger being *unreachable* is not the same as having no
  // Calling — an offline player has always been allowed to drop as a local guest, and
  // gating on a null read would lock them out of their own game. But the ledger being
  // *unread* is not permission either, or the button is live for the second the first
  // fetch takes.
  const callingId = props.calling;
  const ledgerSilent = props.progressLoaded && callingId === null;
  const callingDef: CallingDef | null =
    callingId !== null && callingId !== 0 ? (CALLINGS[callingId] ?? null) : null;
  const callingOk = ledgerSilent || callingDef !== null;
  const mapOk = chosen !== null;
  const ready = callingOk && mapOk;

  const reason =
    !props.progressLoaded && callingDef === null
      ? 'Reading the ledger…'
      : !callingOk
        ? 'Step I first — a Calling decides what you can do out there.'
        : !mapOk
          ? 'No ground is surveyed yet. Nothing to drop onto.'
          : '';

  const rows = carrying(items ?? []);
  const satchelUsed = (items ?? []).filter((s) => s.location === ItemLocation.Satchel).length;
  const quickUsed = (items ?? []).filter((s) => s.location === ItemLocation.Quick).length;
  const insuredCount = rows.filter((r) => r.insured).length;

  const go = (): void => {
    if (!chosen || !ready) return;
    // The sent mode can never diverge from what is playable, whatever the draft holds, and
    // squads are not live: the host is handed an empty squad string on purpose.
    const req = { mode: playableMode(deploy.draft.mode), mapId: chosen.id, squad: '' };
    deploy.remember(req);
    props.host.onDeploy(req);
  };

  const again = (): void => {
    const last = deploy.last;
    if (!last) return;
    // A map can be retired — or hidden behind ?debug — between sessions; fall back to the
    // current pick rather than handing the host a mapId the handshake will refuse.
    const mapId = maps.some((m) => m.id === last.mapId) ? last.mapId : (chosen?.id ?? '');
    if (mapId.length === 0) return;
    const req = { mode: playableMode(last.mode), mapId, squad: '' };
    deploy.remember(req);
    props.host.onDeploy(req);
  };

  // "GO AGAIN". The whole hub, for somebody who has already been out there once and came back
  // wanting to do it again — one sash, above the briefing, saying exactly where it sends them.
  // The briefing is still underneath for anyone who wants to change something first.
  const againTarget = deploy.last ?? { mode: deploy.draft.mode, mapId: chosen?.id ?? '' };
  const againLabel = `Go again · ${modeName(playableMode(againTarget.mode))} · ${mapName(againTarget.mapId)}`;

  return (
    <div class="hub-pane hub-pane-deploy">
      {props.onboarded && ready ? (
        <button type="button" class="hub-sash hub-goagain" onClick={deploy.last ? again : go}>
          <Icon name="deploy" size={26} />
          {againLabel}
        </button>
      ) : null}
      <div class="hub-brief">
        <div class="hub-brief-col">
          <section class="hub-section">
            <Cap icon="calling">Who is going</Cap>
            {callingDef ? (
              <>
                <h2 class="hub-who">
                  <Icon
                    name={CALLING_ICON[callingDef.id] ?? 'calling'}
                    size={44}
                    class="hub-who-mark"
                  />
                  {callingDef.name}
                </h2>
                <p class="hub-who-tag">{callingDef.tagline}</p>
                <p class="hub-who-passive">{callingDef.passiveDesc}</p>
                <div class="hub-lines">
                  {callingDef.abilities.map((a) =>
                    a ? (
                      <div key={a.id} class="hub-line">
                        <span class="hub-line-k">
                          <Icon name={isIconName(a.id) ? a.id : 'pip'} />
                          {a.name}
                        </span>
                        <span class="hub-line-v">{a.desc}</span>
                      </div>
                    ) : null,
                  )}
                </div>
              </>
            ) : (
              <>
                <h2 class="hub-who hub-who-none">No Calling</h2>
                <p class="hub-who-tag">
                  {!props.progressLoaded
                    ? 'Reading the ledger…'
                    : ledgerSilent
                      ? 'The ledger is silent, so nobody knows who you are today.'
                      : 'Nobody knows what to expect of you out there.'}
                </p>
              </>
            )}
            <button
              type="button"
              class="hub-btn hub-btn-quiet"
              onClick={() => {
                ctl.setTab('calling');
              }}
            >
              <Icon name="calling" />
              {callingDef ? 'Change Calling' : 'Choose a Calling'}
            </button>
          </section>

          <section class="hub-section">
            <Cap icon="loadout">Carrying</Cap>
            {rows.length === 0 ? (
              <p class="hub-notice">
                {items === null
                  ? 'The stash is not answering — you will drop with whatever the server says you own.'
                  : 'Nothing but the clothes. Step II fills your hands.'}
              </p>
            ) : (
              <div class="hub-lines">
                {rows.map((r, i) => (
                  <div key={`${r.name}:${String(i)}`} class="hub-line">
                    <span class="hub-line-k">
                      <Icon name={r.icon} />
                      {r.name}
                    </span>
                    <span class="hub-line-v">
                      {r.meta}
                      {r.insured ? ' · insured' : ''}
                      {r.insured ? <Icon name="insurance" size={14} /> : null}
                    </span>
                  </div>
                ))}
                <div class="hub-line">
                  <span class="hub-line-k">
                    <Icon name="satchel" />
                    Secured satchel
                  </span>
                  <span class="hub-line-v">
                    {`${String(SATCHEL_SLOTS - satchelUsed)} of ${String(SATCHEL_SLOTS)} free`}
                  </span>
                </div>
              </div>
            )}
            <button
              type="button"
              class="hub-btn hub-btn-quiet"
              onClick={() => {
                ctl.setTab('stash');
              }}
            >
              <Icon name="loadout" />
              Change loadout
            </button>
          </section>
        </div>

        <div class="hub-brief-col">
          <section class="hub-section">
            <Cap icon="ground">Where</Cap>
            <div class="hub-lines">
              <div class="hub-line">
                <span class="hub-line-k">
                  <Icon name="expedition" />
                  {modeName(PLAYABLE_MODE)}
                </span>
                <span class="hub-line-v">one to four, Hymns only</span>
              </div>
              <div class="hub-line">
                <span class="hub-line-k">
                  <Icon name="ground" />
                  {chosen?.name ?? 'No ground'}
                </span>
                <span class="hub-line-v">
                  {chosen
                    ? `${String(chosen.spawns.length)} drop points${
                        typeof chosen.raidDurationMs === 'number'
                          ? ` · ${String(Math.round(chosen.raidDurationMs / 60000))} min`
                          : ''
                      }`
                    : 'nothing surveyed'}
                </span>
              </div>
              <div class="hub-line">
                <span class="hub-line-k">
                  <Icon name="squad" />
                  Squad
                </span>
                <span class="hub-line-v">
                  {'alone · '}
                  <span class="hub-soon">not open yet</span>
                </span>
              </div>
            </div>
            {/* Offering a door that is bricked up is worse than not mentioning it: while step III
                is shut there is exactly one ground, and the tab this would open bounces straight
                back to Deploy. It returns with Ground, at level 2. */}
            {props.groundLocked ? (
              <p class="hub-notice">
                One ground is surveyed. More opens as you go — the Vigil Downs is where everybody
                starts.
              </p>
            ) : (
              <button
                type="button"
                class="hub-btn hub-btn-quiet"
                onClick={() => {
                  ctl.setTab('ground');
                }}
              >
                <Icon name="ground" />
                Change ground
              </button>
            )}
          </section>

          <section class="hub-section">
            <Cap icon="deploy">Go</Cap>
            <div class="hub-lines">
              <div class={`hub-line${callingOk ? ' ok' : ' no'}`}>
                <span class="hub-line-k">
                  <Icon name="calling" />
                  Calling
                </span>
                <span class="hub-line-v">
                  {callingDef?.name ??
                    (!props.progressLoaded
                      ? 'reading…'
                      : ledgerSilent
                        ? 'ledger silent'
                        : 'not chosen')}
                </span>
              </div>
              <div class={`hub-line${rows.length > 0 ? ' ok' : ''}`}>
                <span class="hub-line-k">
                  <Icon name="loadout" />
                  Loadout
                </span>
                <span class="hub-line-v">
                  {rows.length > 0
                    ? `${String(rows.length)} carried · ${String(quickUsed)}/${String(QUICK_SLOTS)} quick`
                    : 'empty hands'}
                </span>
              </div>
              <div class={`hub-line${insuredCount > 0 ? ' ok' : ''}`}>
                <span class="hub-line-k">
                  <Icon name="insurance" />
                  Insurance
                </span>
                <span class="hub-line-v">
                  {insuredCount > 0 ? `${String(insuredCount)} covered` : 'nothing covered'}
                </span>
              </div>
              <div class={`hub-line${mapOk ? ' ok' : ' no'}`}>
                <span class="hub-line-k">
                  <Icon name="ground" />
                  Ground
                </span>
                <span class="hub-line-v">{groundSummary(deploy.draft)}</span>
              </div>
            </div>

            <button
              type="button"
              class="hub-sash hub-deploy"
              disabled={!ready}
              onClick={go}
              aria-describedby={reason.length > 0 ? 'hub-gate' : undefined}
            >
              <Icon name="deploy" size={22} />
              Deploy
            </button>
            {reason.length > 0 ? (
              <p class="hub-gate" id="hub-gate">
                {reason}
              </p>
            ) : null}
            {/* The GO AGAIN sash at the top already says this when it is showing; a third
                deploy control on one screen was one too many for a first visit. */}
            {props.onboarded && ready ? null : (
              <button
                type="button"
                class="hub-btn hub-btn-wide"
                disabled={deploy.last === null}
                onClick={again}
              >
                <Icon name="reload" />
                {deploy.last
                  ? `Same as last time · ${modeName(playableMode(deploy.last.mode))} · ${mapName(deploy.last.mapId)}`
                  : 'Same as last time'}
              </button>
            )}
            <p class="hub-notice">
              Everything above rides with the deploy. Death drops what you carry, except the
              satchel.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
